import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListTodo, Calendar, Users, BarChart3, CheckCircle2, Clock, Zap, MessageSquare, Target, TrendingUp, AlertCircle, ArrowRight } from 'lucide-react'
import { AddButton, ActionControls, Modal, ConfirmDialog, FormInput, FormSelect } from '../components/common/CRUDControls'

export default function ProjectManagementPage() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    activeProjects: 34,
    tasksOverdue: 8,
    teamMembers: 156,
    completionRate: 87,
  })

  // Projects State
  const [activeProjects, setActiveProjects] = useState([
    { id: 1, name: 'Website Redesign', status: 'On Track', progress: 75, team: 'Design Team' },
    { id: 2, name: 'Mobile App Launch', status: 'Nearly Done', progress: 90, team: 'Dev Team' },
    { id: 3, name: 'API Integration', status: 'At Risk', progress: 45, team: 'Backend Team' },
  ])

  // Modal States
  const [showAddProject, setShowAddProject] = useState(false)
  const [showEditProject, setShowEditProject] = useState(false)
  const [showDeleteProject, setShowDeleteProject] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [projectForm, setProjectForm] = useState({ name: '', status: 'On Track', progress: 0, team: 'Development' })

  // Project Handlers
  const handleAddProject = () => {
    setProjectForm({ name: '', status: 'On Track', progress: 0, team: 'Development' })
    setSelectedProject(null)
    setShowAddProject(true)
  }

  const handleEditProject = (project) => {
    setSelectedProject(project)
    setProjectForm(project)
    setShowEditProject(true)
  }

  const handleDeleteProjectClick = (project) => {
    setSelectedProject(project)
    setShowDeleteProject(true)
  }

  const handleSaveProject = () => {
    if (selectedProject) {
      setActiveProjects(activeProjects.map(p => p.id === selectedProject.id ? { ...p, ...projectForm } : p))
      setShowEditProject(false)
    } else {
      setActiveProjects([...activeProjects, { id: Date.now(), ...projectForm }])
      setShowAddProject(false)
      setStats({ ...stats, activeProjects: stats.activeProjects + 1 })
    }
  }

  const handleConfirmDelete = () => {
    setActiveProjects(activeProjects.filter(p => p.id !== selectedProject.id))
    setShowDeleteProject(false)
    setStats({ ...stats, activeProjects: stats.activeProjects - 1 })
  }

  const features = [
    {
      title: '📊 Project Dashboard',
      description: 'Get a bird\'s eye view of all active projects with real-time status updates',
      icon: BarChart3,
      color: 'from-blue-600 to-blue-500',
      stats: '34 Active Projects',
      path: '/projects'
    },
    {
      title: '✓ Task Management',
      description: 'Create, assign, and track tasks with due dates and priorities',
      icon: CheckCircle2,
      color: 'from-cyan-600 to-cyan-500',
      stats: '847 Total Tasks',
      path: '/tasks'
    },
    {
      title: '📅 Gantt Charts',
      description: 'Visualize project timelines and dependencies with interactive Gantt charts',
      icon: Calendar,
      color: 'from-indigo-600 to-indigo-500',
      stats: '34 Timelines',
      path: '/gantt'
    },
    {
      title: '📋 Kanban Boards',
      description: 'Organize tasks with visual kanban boards for better workflow management',
      icon: ListTodo,
      color: 'from-teal-600 to-teal-500',
      stats: '156 Boards',
      path: '/kanban'
    },
    {
      title: '👥 Team Collaboration',
      description: 'Built-in chat, comments, file sharing, and real-time collaboration tools',
      icon: Users,
      color: 'from-purple-600 to-purple-500',
      stats: '156 Team Members',
      path: '/collaboration'
    },
    {
      title: '⏱️ Time Tracking',
      description: 'Track time spent on tasks, generate timesheets, and measure productivity',
      icon: Clock,
      color: 'from-orange-600 to-orange-500',
      stats: '2,847 Hours Logged',
      path: '/time-tracking'
    },
    {
      title: '📈 Progress Tracking',
      description: 'Monitor project progress, milestones, and resource allocation in real-time',
      icon: TrendingUp,
      color: 'from-pink-600 to-pink-500',
      stats: '87% Completion',
      path: '/progress'
    },
    {
      title: '🎯 Resource Planning',
      description: 'Allocate team members, manage workload, and prevent burnout',
      icon: Target,
      color: 'from-amber-600 to-amber-500',
      stats: 'Balanced Load',
      path: '/resources'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <ListTodo className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Project & Task Management</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Complete Project Management & Team Collaboration Platform</p>
                <p className="text-blue-200 text-sm mt-1">Asana/Monday.com Level Features - Gantt, Kanban, Collaboration & More</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.completionRate}%</div>
                <div className="text-sm text-blue-100">Avg Completion</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Active Projects', value: stats.activeProjects, icon: ListTodo, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Overdue Tasks', value: stats.tasksOverdue, icon: AlertCircle, color: 'from-orange-600 to-orange-500', textColor: 'text-orange-400' },
            { label: 'Team Members', value: stats.teamMembers, icon: Users, color: 'from-indigo-600 to-indigo-500', textColor: 'text-indigo-400' },
            { label: 'Completion Rate', value: `${stats.completionRate}%`, icon: CheckCircle2, color: 'from-teal-600 to-teal-500', textColor: 'text-teal-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Project Management Features */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <ListTodo className="w-7 h-7 text-blue-400" />
            <span>Project Management Tools</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div
                  key={idx}
                  onClick={() => navigate(feature.path)}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all cursor-pointer shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1"
                >
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>

                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ArrowRight className="w-4 h-4 text-blue-400" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Manage Projects */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
              <ListTodo className="w-6 h-6 text-blue-400" />
              <span>Manage Projects</span>
            </h2>
            <AddButton onClick={handleAddProject} label="Add Project" />
          </div>

          <div className="space-y-3">
            {activeProjects.map((project) => (
              <div key={project.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20 flex justify-between items-center">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{project.name}</p>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      project.status === 'On Track' ? 'bg-green-500/20 text-green-300' :
                      project.status === 'Nearly Done' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-orange-500/20 text-orange-300'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-2 mb-2">
                    <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{width: `${project.progress}%`}}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">{project.progress}% Complete</span>
                    <span className="text-blue-400 text-xs">{project.team}</span>
                  </div>
                </div>
                <ActionControls
                  onEdit={() => handleEditProject(project)}
                  onDelete={() => handleDeleteProjectClick(project)}
                  size="lg"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Project Overview & Team Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Projects */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <ListTodo className="w-5 h-5 text-blue-400" />
              <span>Active Projects Summary</span>
            </h3>
            <div className="space-y-3">
              {activeProjects.slice(0, 3).map((project) => (
                <div key={project.id} className="bg-slate-700/50 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-semibold text-sm">{project.name}</p>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      project.status === 'On Track' ? 'bg-green-500/20 text-green-300' :
                      project.status === 'Nearly Done' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-orange-500/20 text-orange-300'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-2 mb-2">
                    <div className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{width: `${project.progress}%`}}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">{project.progress}% Complete</span>
                    <span className="text-blue-400 text-xs">{project.team}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Calendar className="w-5 h-5 text-blue-400" />
              <span>Upcoming Deadlines</span>
            </h3>
            <div className="space-y-3">
              {[
                { task: 'Design Approval', dueDate: 'Today', priority: 'HIGH', assignee: 'Sarah' },
                { task: 'Code Review', dueDate: '2 days', priority: 'HIGH', assignee: 'Mike' },
                { task: 'Client Feedback', dueDate: '5 days', priority: 'MEDIUM', assignee: 'Jane' },
                { task: 'Testing Phase', dueDate: '1 week', priority: 'MEDIUM', assignee: 'Team' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white font-semibold text-sm">{item.task}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      item.priority === 'HIGH' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-500/20 text-yellow-300'
                    }`}>
                      {item.priority}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-300 text-xs">Due: {item.dueDate}</span>
                    <span className="text-cyan-400 text-xs font-semibold">{item.assignee}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Project Modal */}
        <Modal
          isOpen={showAddProject || showEditProject}
          onClose={() => {
            setShowAddProject(false)
            setShowEditProject(false)
          }}
          title={selectedProject ? 'Edit Project' : 'Add New Project'}
          onSubmit={handleSaveProject}
          submitLabel={selectedProject ? 'Update' : 'Add'}
        >
          <FormInput
            label="Project Name"
            value={projectForm.name}
            onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
            placeholder="e.g., Website Redesign"
            required
          />
          <FormSelect
            label="Status"
            value={projectForm.status}
            onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
            options={[
              { value: 'On Track', label: 'On Track' },
              { value: 'Nearly Done', label: 'Nearly Done' },
              { value: 'At Risk', label: 'At Risk' },
              { value: 'On Hold', label: 'On Hold' },
            ]}
          />
          <FormInput
            label="Progress (%)"
            type="number"
            value={projectForm.progress}
            onChange={(e) => setProjectForm({ ...projectForm, progress: parseInt(e.target.value) || 0 })}
            required
          />
          <FormInput
            label="Team"
            value={projectForm.team}
            onChange={(e) => setProjectForm({ ...projectForm, team: e.target.value })}
            placeholder="e.g., Development Team"
          />
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteProject}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteProject(false)}
          title="Delete Project"
          message={`Are you sure you want to delete "${selectedProject?.name}"? This action cannot be undone.`}
        />

      </div>
    </div>
  )
}
